import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { InteractionManager, StyleSheet, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import Animated, { Extrapolate, interpolate, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import { isSameDay } from 'date-fns';
import { Indicator } from 'design-system/Indicator';
import { AppView } from 'design-system/AppView';
import ScrollView from 'design-system/ScrollView';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { getCategories, getLinkedTransaction, getMerchantStats, getOriginalSplit, getTransaction, getTransactionReceipts, resetTransaction, getReward, cacheSharedBillInfo, clearSharedBillInfoCache, } from 'actions';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import { getSubscriptionsById } from 'features/subscriptions/actions';
import { selectIncludeIncomeSubscriptions } from 'features/subscriptions/selectors';
import { muteAll, useFetchOld, useTimeout } from 'hooks';
import useAppRating from 'hooks/useAppRating';
import { useCategories } from 'hooks/useCategory';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectAccountById } from 'reducers/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import Amplitude from 'utils/amplitude';
import { getPaymentRequestInfo } from 'features/pay/actions';
import TouchableOpacity from 'utils/packages/TouchableOpacity';
import useWalkthroughLayout from 'features/feed/components/walkthrough/useWalkthroughLayout';
import Walkthrough from 'features/feed/components/walkthrough/Walkthrough';
import WalkthroughOverlay from 'features/feed/components/walkthrough/WalkthroughOverlay';
import { useIsMainAppWalkthroughV6Active } from 'features/feed/components/walkthrough/hooks/useIsMainAppWalkthroughV6Active';
import { useShowWalkthroughV6 } from 'features/feed/components/walkthrough/hooks/useShowWalkthroughV6';
import TransactionHeader, { useTransactionHeaderHeight, useTransactionHeaderUpgradeButtons, } from '../components/detail/TransactionHeader';
import { isWeb } from '../../../constants';
import { CreateGroupCard } from '../components/CreateGroupCard';
import TransactionCategorySection from '../components/detail/TransactionCategorySection';
import TransactionDateButton from '../components/detail/TransactionDateButton';
import TransactionDetailContent from '../components/detail/TransactionDetailContent';
import TransactionFooter from '../components/detail/TransactionFooter';
import TransactionPendingSection from '../components/detail/TransactionPendingSection';
import { makeSelectTransactionById, selectOptimisedTransactionScreen, } from '../selectors';
import TransactionEditToast from '../components/TransactionEditToast';
import TransactionNameCopiedToast from '../components/TransactionNameCopiedToast';
import TransactionTopSection from '../components/detail/TransactionTopSection';
const shadowOpacityOutputRange = [0, 0, 1];
const TransactionScreen = ({ navigation, route }) => {
    const { skipGetSubscription, id } = route.params;
    const colors = useColors();
    const contentContainerStyles = useMarginBottom('paddingBottom');
    const [state, setState] = useState({ reward: null });
    const dispatch = useAppDispatch();
    const [, , , fetch] = useFetchOld(muteAll);
    const rateApp = useAppRating();
    useEffect(() => {
        const unsubscribe = navigation.addListener('beforeRemove', (event) => {
            const { action } = event.data;
            if (action.type === 'GO_BACK' || action.type === 'POP') {
                rateApp({ marketplaceReview: true });
            }
        });
        return unsubscribe;
    }, [navigation, rateApp]);
    const showWalkthroughV6 = useShowWalkthroughV6();
    const isMainAppWalkthroughV6Active = useIsMainAppWalkthroughV6Active();
    const memoSelectTransactionById = useMemo(makeSelectTransactionById, []);
    const transaction = useAppSelector((store) => memoSelectTransactionById(store, id));
    const includeIncome = useAppSelector(selectIncludeIncomeSubscriptions);
    const store = useAppStore();
    const [toastVisible, setToastVisible] = useState(false);
    const isFocused = useIsFocused();
    const lastCategoryEditToastTime = useAppSelector((state) => state.deviceSettings.lastCategoryEditToastTime);
    useTimeout(() => {
        setToastVisible(false);
    }, 
    // Count down only once this screen is back on top, e.g. after a tag edit
    toastVisible && isFocused ? 4000 : undefined // 4 seconds
    );
    const [copyToastVisible, setCopyToastVisible] = useState(false);
    useTimeout(() => setCopyToastVisible(false), copyToastVisible ? 4000 : undefined);
    const showToast = useCallback(() => {
        const now = new Date();
        const lastShown = lastCategoryEditToastTime
            ? new Date(lastCategoryEditToastTime)
            : null;
        if (!lastShown || !isSameDay(now, lastShown)) {
            setToastVisible(true);
            dispatch(setDeviceSettingsKey('lastCategoryEditToastTime', now.toISOString()));
        }
    }, [lastCategoryEditToastTime, dispatch]);
    const defaultCategories = useCategories();
    const hasGroups = useAppSelector((store) => store.groups.groups.length > 0);
    const isHolidays = transaction
        ? transaction.category.id === defaultCategories.holidays.id ||
            transaction.category.parentCategoryId === defaultCategories.holidays.id
        : false;
    const noCreateGroupCard = !isHolidays || hasGroups;
    useEffect(() => {
        const id = transaction?.id || route.params.id;
        const isCompact = transaction && 'isCompact' in transaction;
        const wasCompact = isCompact;
        const getRelatedAndSplit = (transaction) => {
            if ('relatedTransactionId' in transaction &&
                transaction.relatedTransactionId) {
                dispatch(getLinkedTransaction(transaction.relatedTransactionId));
            }
            if ('splitFromId' in transaction && transaction.splitFromId) {
                dispatch(getOriginalSplit(transaction.splitFromId));
            }
        };
        InteractionManager.runAfterInteractions(() => {
            if (!wasCompact && transaction) {
                getRelatedAndSplit(transaction);
            }
            Amplitude.logEvent('Transaction.Viewed');
            dispatch(resetTransaction());
            fetch(getTransaction(id, includeIncome)).then((response) => {
                if (response) {
                    const { merchantId, merchant, subscriptionId, paymentActivities } = response;
                    if (!response.isPending) {
                        if (wasCompact) {
                            getRelatedAndSplit(response);
                        }
                        if (merchant?.rewardId !== undefined &&
                            merchant?.rewardId !== null) {
                            fetch(getReward(merchant.rewardId)).then((response) => {
                                if (response) {
                                    setState((s) => ({ ...s, reward: response }));
                                }
                            });
                        }
                        dispatch(getTransactionReceipts(id));
                        if (merchantId)
                            dispatch(getMerchantStats(merchantId));
                        if (subscriptionId && !skipGetSubscription) {
                            dispatch(getSubscriptionsById(subscriptionId));
                        }
                        if (paymentActivities) {
                            Promise.all(paymentActivities.map(async (payment) => {
                                if (!payment.paymentRequestToken)
                                    return payment;
                                const info = await dispatch(getPaymentRequestInfo(payment.paymentRequestToken));
                                return {
                                    ...payment,
                                    link: info?.payload?.paymentRequest?.link,
                                };
                            })).then((billSharingPaymentActivities) => {
                                dispatch(cacheSharedBillInfo(id, billSharingPaymentActivities));
                            });
                        }
                        else {
                            dispatch(clearSharedBillInfoCache(id));
                        }
                    }
                }
                return response;
            });
            const { categories } = store.getState().user;
            if (categories && !categories.length)
                dispatch(getCategories());
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onChangeDate = useCallback(() => {
        if (!transaction || transaction.isPending)
            return;
        navigation.navigateDeprecated('EditDate', {
            id: transaction.id,
        });
    }, [transaction]);
    const onRenameTransaction = useCallback(() => {
        if (!transaction || transaction.isPending)
            return;
        navigation.navigateDeprecated('RenameTransaction', {
            id: transaction.id,
        });
    }, [transaction]);
    const account = useAppSelector((store) => selectAccountById(store, transaction?.accountId));
    const scrollY = useSharedValue(0);
    const { showFirstUpgradeButton, showRenameUpgradeButton, useCustomName, onPressUpgrade, } = useTransactionHeaderUpgradeButtons(transaction);
    const [transactionHeaderHeightExpanded, transactionHeaderHeightFinal, scrollOffsetHeight, headerScrollOffset,] = useTransactionHeaderHeight(transaction, showRenameUpgradeButton, showFirstUpgradeButton);
    const animationInputRange = useMemo(() => [0, scrollOffsetHeight, scrollOffsetHeight + 1], [scrollOffsetHeight]);
    const bgColorOutputRange = useMemo(() => [
        colors.background.dark,
        colors.background.dark,
        colors.cards.onDark,
    ], [colors.background.dark, colors.cards.onDark]);
    const translateStyles = useAnimatedStyle(() => ({
        transform: [
            {
                translateY: interpolate(scrollY.value, [0, headerScrollOffset, headerScrollOffset + 1], [0, -headerScrollOffset, -headerScrollOffset], Extrapolate.CLAMP),
            },
        ],
    }), [headerScrollOffset]);
    useLayoutEffect(() => {
        const nameToDisplay = useCustomName
            ? transaction?.customName || transaction?.counterpartName
            : transaction?.counterpartName;
        navigation.setOptions({
            headerTitle: () => (<Animated.View style={translateStyles} pointerEvents={isMainAppWalkthroughV6Active ? 'none' : 'auto'}>
          <View style={styles.transactionDateContainer}>
            <TransactionDateButton transaction={transaction} onPressDate={onChangeDate} disabled={transaction?.isPending}/>
            {!!isMainAppWalkthroughV6Active && <WalkthroughOverlay />}
          </View>
        </Animated.View>),
            headerRight: () => !transaction?.isPending ? (<TouchableOpacity hoverOnDark onCard onPress={onRenameTransaction} style={styles.headerRightBtn} disabled={isMainAppWalkthroughV6Active}>
            <Text Text-14 color={isMainAppWalkthroughV6Active
                    ? colors.elements.primaryOnModal
                    : colors.text.primary}>
              Rename
            </Text>
          </TouchableOpacity>) : null,
            title: nameToDisplay,
            headerTransparent: true,
            headerTintColor: (isMainAppWalkthroughV6Active
                ? colors.elements.primaryOnModal
                : colors.text.primary),
        });
    }, [
        colors.elements.primaryOnModal,
        colors.text.primary,
        isMainAppWalkthroughV6Active,
        onChangeDate,
        onRenameTransaction,
        transaction,
        translateStyles,
        useCustomName,
    ]);
    const enableOptimisedTransactionScreen = useAppSelector(selectOptimisedTransactionScreen);
    const [didRender, setDidRender] = useState(isWeb || !enableOptimisedTransactionScreen);
    useEffect(() => {
        if (didRender)
            return () => { };
        const timeout = setTimeout(() => setDidRender(true));
        return () => clearTimeout(timeout);
    }, [didRender]);
    const { itemPositions, scrollHeight, scrollLayoutHeight } = useWalkthroughLayout();
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      {transaction ? (<>
          <ScrollView secondary finalHeaderHeight={transactionHeaderHeightFinal} expandedHeaderHeight={transactionHeaderHeightExpanded} animationInputRange={animationInputRange} shadowOpacityOutputRange={shadowOpacityOutputRange} bgColorOutputRange={bgColorOutputRange} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyles} scrollY={scrollY} isInTab={isWeb}>
            <TransactionTopSection transaction={transaction}/>
            <TransactionCategorySection transaction={transaction} onCategoryEdit={showToast}/>
            {!noCreateGroupCard && <CreateGroupCard />}
            {transaction?.isPending ? (<TransactionPendingSection />) : (<TransactionDetailContent transaction={transaction} reward={state.reward} skipGetSubscription={!!skipGetSubscription} didRender={didRender} onTagsEdit={showToast}/>)}
            {!!didRender && (<TransactionFooter isPending={!!transaction.isPending} realCounterpartName={'realCounterpartName' in transaction
                    ? transaction.realCounterpartName
                    : null} account={account} onCopy={() => setCopyToastVisible(true)}/>)}
          </ScrollView>
          <TransactionHeader scrollY={scrollY} headerScrollOffset={headerScrollOffset} transaction={transaction} showRenameUpgradeButton={showRenameUpgradeButton} showFirstUpgradeButton={!!showFirstUpgradeButton} useCustomName={useCustomName} onPressUpgrade={onPressUpgrade}/>
          {!!didRender && (<TransactionEditToast isVisible={toastVisible} onClose={() => setToastVisible(false)}/>)}
          {!!didRender && (<TransactionNameCopiedToast isVisible={copyToastVisible} onClose={() => setCopyToastVisible(false)}/>)}
          {!!showWalkthroughV6 && (<Walkthrough scrollHeight={scrollHeight} itemPositions={itemPositions} scrollLayoutHeight={scrollLayoutHeight}/>)}
        </>) : (<View style={styles.indicatorContainer}>
          <Indicator dark/>
        </View>)}
    </AppView>);
};
const styles = StyleSheet.create({
    headerRightBtn: {
        ...(isWeb && {
            marginRight: rem(16),
            paddingVertical: rem(8),
            paddingHorizontal: rem(16),
        }),
    },
    indicatorContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    transactionDateContainer: {
        borderRadius: rem(8),
        overflow: 'hidden',
    },
});
export default memo(TransactionScreen);
